import { FormGroup } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputTextField } from '../../../../shared/forms/fields/InputTextField';
import { SwitchField } from '../../../../shared/forms/fields/SwitchField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import { useEditorStore } from '../../../store';

interface ImageAttributes {
  src?: string;
  includesRedirection?: boolean;
}

interface ImageI18nAttributes {
  src?: string;
  href?: string;
}

type FormValue = TraitFormValue<ImageAttributes, ImageI18nAttributes>;

/**
 * Saving traits replaces the i18n attributes of the current locale, so the src has to be part of
 * the form value, otherwise the image is lost. It is kept in sync with the selected component
 * because the form is reset with a possibly outdated value on every sidebar render.
 */
function usePreserveImageSrc() {
  const { setValue, watch } = useFormContext<FormValue>();
  const selectedComponent = useEditorStore(state => state.selectedComponent);
  const locale = useEditorStore(state => state.locale);

  const componentSrc =
    selectedComponent?.attributes.i18nAttributes?.[locale]?.src ||
    selectedComponent?.attributes.attributes?.src;
  const formSrc = watch('i18nAttributes.src');

  useEffect(() => {
    if (componentSrc && formSrc !== componentSrc) {
      setValue('i18nAttributes.src', componentSrc);
    }
  }, [componentSrc, formSrc, setValue]);
}

export function ImageWidgetTraits() {
  const { t } = useTranslation();
  const { watch } = useFormContext<FormValue>();

  usePreserveImageSrc();

  const includesRedirection = watch('attributes.includesRedirection');

  return (
    <FormGroup>
      <Trait>
        <SwitchField<FormValue>
          name="attributes.includesRedirection"
          label={t('editor.image.traits.includesRedirection')}
        />
      </Trait>
      {includesRedirection && (
        <Trait>
          <InputTextField<FormValue>
            type="text"
            name="i18nAttributes.href"
            label={t('editor.image.traits.url')}
            placeholder={t('editor.image.traits.url.placeholder')}
          />
        </Trait>
      )}
    </FormGroup>
  );
}
